import { ContentService } from '@blk/cwp-ng-framework';
import * as unzip from 'lodash/unzip';

import { CellUtil } from '../../utils/cell-util';
import { CellAttributes } from '../cell-attributes';
import { CellData } from '../cell-data';
import { ColumnConfig } from '../column-config';
import { ColumnText } from '../column-text';
import { ColumnExtractor } from '../extractors/column-extractor';
import { StoreColumnExtractor } from '../extractors/store-column-extractor';
import { TableData } from '../table-data';
import { TableDataTransform } from '../table-data-transform/table-data-transform';

export abstract class ExtractorConfig {
	constructor(public readonly portfolioName: ColumnConfig<StoreColumnExtractor>, public readonly tableDataTransform: TableDataTransform) { }
	public abstract storeDependencies(): string[];

	public abstract extract(storeLookup: Map<any, any>, contentSvc: ContentService): TableData[];

	protected extractStoreColumn(column: ColumnConfig<StoreColumnExtractor>, storeLookup: Map<string, any[]>, contentSvc: ContentService): CellData[] {
		column = CellUtil.translateColumnConfig(column, contentSvc);
		const dataArgs = column.extractor.storeConfigDependencies().map(config => storeLookup.get(config));
		const portsDataArgs = unzip(dataArgs);
		const extracted = portsDataArgs.map(portfolioData => column.extractor.extractor()(...portfolioData));
		const columnData: CellData[] = extracted.map(
			value => new CellData(column, value, column.labelFormatter.format(value, contentSvc), null, this.generateAttributes(value, column, contentSvc))
		);
		return columnData;
	}

	protected extractDataColumn(column: ColumnConfig<ColumnExtractor>, data: any[], contentSvc: ContentService, parent: CellData[] = null): CellData {
		column = CellUtil.translateColumnConfig(column, contentSvc);
		const value = column.extractor.extractor()(data);
		return new CellData(column, value, column.labelFormatter.format(value, contentSvc), parent, this.generateAttributes(value, column, contentSvc), []);
	}

	protected generateColumnHeaders(columns: ColumnConfig<any>[], contentSvc: ContentService): CellData[] {
		return columns.map(config => {
			config = CellUtil.translateColumnConfig(config, contentSvc);
			const label = this.translate(config.label, contentSvc);
			const attributes = config && config.label && config.label.attributes ? config.label.attributes : {};
			attributes['infoBubble'] = this.translate(config.infoBubble, contentSvc);

			return new CellData(config, label, label, null, attributes);
		});
	}

	protected generateAttributes(value: any, column: ColumnConfig<any>, contentSvc: ContentService): any {
		const attributes = {};

		if (column.formatters) {
			column.formatters.forEach(formatter => {
				attributes[formatter.attributeName()] = formatter.format(value, contentSvc);
			});
		}

		return attributes;
	}

	private translate(text: ColumnText, contentSvc: ContentService): string {
		if (text) {
			if (contentSvc) {
				return contentSvc.get(text.key, text.defaultText);
			} else {
				return text.defaultText;
			}
		}
		return null;
	}

	protected translateColumnText(text: ColumnText, contentSvc: ContentService): CellData {
		const label = this.translate(text, contentSvc);
		const config = text ? new ColumnConfig(new ColumnText(text.key, text.defaultText, label), null, null) : null;
		return new CellData(config, label, label, null, this.translateAttributes(text?.attributes ?? {}, contentSvc));
	}

	private translateAttributes(attributes: CellAttributes, contentSvc: ContentService): CellAttributes {
		for (const [attrKey, attrValue] of Object.entries(attributes)) {
			attributes[attrKey] = attrValue instanceof ColumnText ? new ColumnText(attrValue.key, attrValue.defaultText, this.translate(attrValue, contentSvc)) : attrValue;
		}
		return attributes;
	}
}
